import { type FormEvent, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BadgeEuro,
  BriefcaseBusiness,
  CheckCircle2,
  CircleDollarSign,
  FileText,
  Handshake,
  ListTodo,
  Package,
  Plus,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Truck,
  XCircle,
  Zap,
} from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import type { CustomerListRecord, CustomersGateway } from '@forq/customers-data';
import type {
  SalesGateway,
  SalesHandoffRecord,
  SalesItemOption,
  SalesMachineDetail,
  SalesMachineOption,
  SalesOfferChannel,
  SalesOfferPositionInput,
  SalesOfferPositionRecord,
  SalesOfferRecord,
  SalesOfferStatus,
  SalesOpportunityRecord,
  SalesOpportunityStage,
  SalesOrderDelivery,
  SalesOrderRecord,
  SalesOverview,
  SalesPositionKind,
  SalesSource,
} from '@forq/sales-data';
import { confirmDiscard, describeDeleteOutcome, EmptyState, RecordDeleteButton, SearchSelect, useContextMenu, useRecordDeleteFlow, type ContextMenuItem } from '@forq/ui';
import { type CompanyProfileGateway, type TenantCompanyProfile, emptyCompanyProfile } from '@forq/platform-data';
import { renderSalesDeliveryHtml, renderSalesOfferHtml, renderSalesUvvHtml } from './documents';

type SalesInvoiceLineKind = 'labour' | 'material' | 'travel' | 'service' | 'machine' | 'freight' | 'text' | 'other';

/**
 * Minimal structural view of FORQ Finance the sales module hands a won/direct sale to. Keeps the
 * module free of an apps/web dependency (the concrete FinanceGateway lives in the app); the real
 * gateway satisfies this shape structurally.
 */
interface SalesFinanceHandoffGateway {
  createInvoice(input: {
    readonly tenantId: string;
    readonly customerNumber: string;
    readonly customerName: string;
    readonly sourceType: 'sales';
    readonly sourceNumber: string;
    /** Leistungsdatum (§ 14 UStG) -- beim Verkauf der Liefertag. Leer = die Ablage setzt heute. */
    readonly serviceDate?: string | null;
    readonly lines: readonly {
      readonly kind: SalesInvoiceLineKind;
      readonly description: string;
      readonly quantity: number;
      readonly unit: string;
      readonly unitPrice: number;
      /** Zeilenrabatt in Prozent; fehlt er, gilt 0. */
      readonly discountPercent?: number;
      readonly vatRate: number;
    }[];
  }): Promise<{ readonly proposalNumber: string }>;
  /** Für die Idempotenz der Übergabe: existiert schon eine Rechnung zu diesem Auftrag? */
  listInvoices(input: { readonly tenantId: string }): Promise<readonly {
    readonly sourceType: string;
    readonly sourceNumber: string;
    readonly proposalNumber: string;
  }[]>;
}

/** Nur die Detail-Abrufe, die der Verkauf für die Warengruppen-Auflösung beim Abrechnen braucht --
 *  ohne harte Abhängigkeit des Sales-Moduls auf die Maschinen-/Artikel-Pakete. Die echten Gateways
 *  erfüllen diese Formen strukturell. */
interface MachineWarengruppeLookup {
  getMachine(tenantId: string, machineId: string): Promise<{ readonly warengruppeId?: string | null; readonly warengruppeName?: string }>;
}
interface ItemWarengruppeLookup {
  getItem(tenantId: string, itemId: string): Promise<{ readonly warengruppeId?: string | null; readonly warengruppeName?: string }>;
}
interface WarengruppenSource {
  listWarengruppen(tenantId: string): Promise<readonly { readonly id: string; readonly name: string }[]>;
}

interface SalesPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: SalesGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly customersGateway: CustomersGateway;
  readonly financeGateway: SalesFinanceHandoffGateway;
  /** Firmenprofil für den Briefkopf des Lieferscheins. */
  readonly companyGateway: CompanyProfileGateway;
  /** Für die Warengruppe je Rechnungszeile (Stufe 3): löst Maschine/Artikel der Position auf. */
  readonly machinesGateway: MachineWarengruppeLookup;
  readonly masterdataGateway: ItemWarengruppeLookup;
  /** Warengruppen-Stamm für den Picker an der Verkaufsposition. */
  readonly mastersGateway: WarengruppenSource;
  /** One-shot action from the Sales-Center shell (header/cockpit) — opens the matching dialog. */
  readonly pendingAction?: string | null;
  readonly onActionConsumed?: () => void;
}

type SalesSection = 'overview' | 'pipeline' | 'offers' | 'orders' | 'handoffs';

interface PositionDraft {
  readonly id: string;
  kind: SalesPositionKind;
  sourceId: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  purchasePrice: string;
  discountPercent: string;
  vatRate: string;
  warengruppeId: string;
}

const sections: readonly {
  readonly id: SalesSection;
  readonly featureId: string;
  readonly label: string;
  readonly icon: typeof FileText;
}[] = [
  { id: 'overview', featureId: 'sales.overview', label: 'Übersicht', icon: BadgeEuro },
  { id: 'pipeline', featureId: 'sales.pipeline', label: 'Pipeline', icon: ListTodo },
  { id: 'offers', featureId: 'sales.offers', label: 'Angebote', icon: FileText },
  { id: 'orders', featureId: 'sales.orders', label: 'Verkaufsaufträge', icon: BriefcaseBusiness },
  { id: 'handoffs', featureId: 'sales.handoffs', label: 'Übergaben', icon: Send },
];

const stageLabels: Record<SalesOpportunityStage, string> = {
  new: 'Neu',
  qualification: 'Qualifizierung',
  'needs-confirmed': 'Bedarf geklärt',
  'offer-planned': 'Angebot geplant',
  'offer-presented': 'Angebot vorgestellt',
  'follow-up': 'Nachfassen',
  won: 'Gewonnen',
  lost: 'Verloren',
};

const sourceLabels: Record<SalesSource, string> = {
  phone: 'Telefon',
  email: 'E-Mail',
  website: 'Website',
  referral: 'Empfehlung',
  marketplace: 'Maschinenbörse',
  'existing-customer': 'Bestandskunde',
  fair: 'Messe',
  other: 'Sonstige',
};

const channelLabels: Record<SalesOfferChannel, string> = {
  phone: 'Telefonisch',
  email: 'E-Mail',
  document: 'Dokument',
  onsite: 'Vor Ort',
};

const offerStatusLabels: Record<SalesOfferStatus, string> = {
  draft: 'Entwurf',
  presented: 'Vorgestellt',
  'follow-up': 'Nachfassen',
  won: 'Gewonnen',
  lost: 'Verloren',
  expired: 'Abgelaufen',
};

const positionKindLabels: Record<SalesPositionKind, string> = {
  machine: 'Maschine',
  article: 'Artikel',
  service: 'Dienstleistung',
  text: 'Freitext',
};

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

// Technische Kennzahlen fürs Kriterien-Matching im Maschinenpicker (Weg A: Verkauf sucht passendes
// Gerät). Schlüssel = machine_type_field-Keys aus dem Maschinenstamm.
const MACHINE_SPEC_FIELDS: readonly { readonly key: string; readonly label: string; readonly unit?: string }[] = [
  { key: 'capacity', label: 'Tragf.', unit: 'kg' },
  { key: 'liftHeight', label: 'Hub', unit: 'mm' },
  { key: 'overallHeight', label: 'Bauh.', unit: 'mm' },
  { key: 'workingHeight', label: 'Arbeitsh.', unit: 'm' },
  { key: 'drive', label: 'Antrieb' },
  { key: 'hours', label: 'Std.', unit: 'h' },
];

/** Kurzer Kennzahlen-Text (Typ · Tragf. · Hub · Antrieb …) -- im Picker als Hint angezeigt UND gefiltert. */
function machineSpecHint(machine: SalesMachineOption): string {
  const specs = MACHINE_SPEC_FIELDS
    .map(({ key, label, unit }) => {
      const value = machine.attributes[key];
      return value ? `${label} ${String(value)}${unit ? ` ${unit}` : ''}` : '';
    })
    .filter(Boolean);
  return [machine.typeName, ...specs].filter(Boolean).join(' · ');
}

function parseNumber(value: FormDataEntryValue | string | null): number {
  return Number(String(value ?? '').replace(',', '.')) || 0;
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : 'Die Sales-Daten konnten nicht verarbeitet werden.';
}

function emptyPosition(): PositionDraft {
  return {
    id: crypto.randomUUID(),
    kind: 'machine',
    sourceId: '',
    description: '',
    quantity: '1',
    unit: 'Stück',
    unitPrice: '0',
    purchasePrice: '0',
    discountPercent: '0',
    vatRate: '19',
    warengruppeId: '',
  };
}

function customerLabel(record: SalesOpportunityRecord | SalesOfferRecord | SalesOrderRecord): string {
  return record.customerName || record.prospectName || 'Ohne Bezeichnung';
}

function buildPositionInputs(
  positions: readonly PositionDraft[],
  machines: readonly SalesMachineOption[],
  items: readonly SalesItemOption[],
): SalesOfferPositionInput[] {
  return positions.map((position) => {
    const machine = position.kind === 'machine' ? machines.find((item) => item.id === position.sourceId) : null;
    const item = position.kind === 'article' || position.kind === 'service'
      ? items.find((candidate) => candidate.id === position.sourceId)
      : null;
    return {
      kind: position.kind,
      sourceId: position.sourceId || null,
      sourceNumber: machine?.number ?? item?.number ?? '',
      machineId: machine?.id ?? null,
      description: position.description,
      quantity: parseNumber(position.quantity),
      unit: position.unit,
      unitPrice: parseNumber(position.unitPrice),
      purchasePrice: parseNumber(position.purchasePrice),
      discountPercent: parseNumber(position.discountPercent),
      vatRate: parseNumber(position.vatRate),
      warengruppeId: position.warengruppeId || null,
    };
  });
}

const invoiceLineKindFor: Record<SalesPositionKind, SalesInvoiceLineKind> = {
  machine: 'machine', article: 'material', service: 'service', text: 'text',
};

/** Turns a won sale's positions into FORQ Finance invoice lines (Rabatt bleibt an der Zeile).
 *  `warengruppen` ist index-gleich zu `positions` (Stufe 3): die echte Warengruppe je Zeile. */
function salesInvoiceLines(
  positions: readonly SalesOfferPositionRecord[],
  warengruppen?: readonly ({ readonly id: string | null; readonly name: string } | null)[],
) {
  // Der Rabatt reist seit 09/2026 an der ZEILE mit. Vorher entstand daraus eine Sammelzeile
  // „Rabatt" mit negativem Preis und dem Steuersatz der ERSTEN Position -- bei einem Angebot mit
  // 19 % und 7 % wurde der ganze Nachlass damit dem falschen Satz zugeordnet.
  const lines = positions.map((position, index) => ({
    kind: invoiceLineKindFor[position.kind],
    description: position.description,
    quantity: position.quantity,
    unit: position.unit,
    unitPrice: position.unitPrice,
    discountPercent: position.kind === 'text' ? 0 : (position.discountPercent ?? 0),
    vatRate: position.vatRate,
    warengruppeId: warengruppen?.[index]?.id ?? null,
    warengruppeName: warengruppen?.[index]?.name ?? '',
  }));
  return lines;
}

/**
 * Picks a customer from the REAL customer base (real-time search) or creates a new one inline
 * (which becomes a proper Stammkunde immediately). Writes the chosen customerNumber/customerName
 * into hidden inputs read from the surrounding dialog's FormData — no dependency on apps/web.
 */
interface PickedCustomer {
  readonly id: string;
  readonly number: string;
  readonly name: string;
}

function CustomerPicker({
  tenantId,
  gateway,
  initialId,
  initialNumber,
  initialName,
  onPick,
}: {
  readonly tenantId: string;
  readonly gateway: CustomersGateway;
  readonly initialId: string;
  readonly initialNumber: string;
  readonly initialName: string;
  /** Meldet die Auswahl nach oben (für die Titel-Vorbelegung des Angebots). */
  readonly onPick?: (customer: PickedCustomer | null) => void;
}) {
  const [picked, setPicked] = useState<PickedCustomer | null>(
    initialNumber ? { id: initialId, number: initialNumber, name: initialName } : null,
  );
  function selectCustomer(next: PickedCustomer | null): void {
    setPicked(next);
    onPick?.(next);
  }
  const [mode, setMode] = useState<'search' | 'create'>('search');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly CustomerListRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const skip = useRef(false);
  const [companyName, setCompanyName] = useState('');
  const [city, setCity] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    const term = query.trim();
    if (!open || !term) { setResults([]); return undefined; }
    const timer = window.setTimeout(() => {
      gateway.searchCustomers({ tenantId, query: term, status: 'all', pageSize: 6 })
        .then((page) => { setResults(page.items); setActive(0); })
        .catch(() => setResults([]));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [query, open, tenantId, gateway]);

  function choose(customer: CustomerListRecord): void {
    skip.current = true;
    selectCustomer({ id: customer.id, number: customer.number, name: customer.displayName });
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((current) => Math.min(current + 1, results.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((current) => Math.max(current - 1, 0)); }
    else if (event.key === 'Enter' && results[active]) { event.preventDefault(); choose(results[active]); }
    else if (event.key === 'Escape') { setOpen(false); }
  }

  async function create(): Promise<void> {
    const name = companyName.trim();
    if (name.length < 2) { setCreateError('Bitte einen Firmennamen angeben.'); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await gateway.createCustomer({
        tenantId,
        kind: 'company',
        companyName: name,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        ...(city.trim() ? { billingAddress: { street: '', postalCode: '', city: city.trim() } } : {}),
      });
      selectCustomer({ id: created.id, number: created.number, name: created.displayName });
      setMode('search');
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Der Kunde konnte nicht angelegt werden.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="sales-customer-picker is-wide">
      <span className="sales-field-label">Kunde (Stammkunde)</span>
      {picked ? (
        <div className="sales-customer-picked">
          <strong>{picked.number} · {picked.name}</strong>
          <button type="button" className="sales-secondary" onClick={() => { selectCustomer(null); setMode('search'); }}>Ändern</button>
        </div>
      ) : mode === 'search' ? (
        <div className="sales-customer-search">
          <div className="customer-search">
            <span className="customer-search__icon"><Search size={15} /></span>
            <input
              type="text"
              value={query}
              placeholder="Kunde suchen – Name, Nummer, Ort …"
              autoComplete="off"
              onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 120)}
              onKeyDown={handleKeyDown}
            />
            {open && results.length > 0 ? (
              <ul className="customer-search__results">
                {results.map((customer, index) => (
                  <li key={customer.id}>
                    <button type="button" className={index === active ? 'is-active' : ''} onMouseDown={(event) => { event.preventDefault(); choose(customer); }} onMouseMove={() => setActive(index)}>
                      <span className="customer-search__name">{customer.displayName}</span>
                      <span className="customer-search__meta">{customer.number}{customer.city ? ` · ${customer.city}` : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <button type="button" className="sales-link" onClick={() => setMode('create')}><Plus size={14} /> Neuen Kunden anlegen</button>
        </div>
      ) : (
        <div className="sales-customer-create">
          <input placeholder="Firmenname" value={companyName} onChange={(event) => setCompanyName(event.target.value)} autoFocus />
          <input placeholder="Ort" value={city} onChange={(event) => setCity(event.target.value)} />
          <input placeholder="E-Mail" value={email} onChange={(event) => setEmail(event.target.value)} />
          <input placeholder="Telefon" value={phone} onChange={(event) => setPhone(event.target.value)} />
          <div className="sales-customer-create__actions">
            <button type="button" className="sales-secondary" onClick={() => { setMode('search'); setCreateError(null); }}>Abbrechen</button>
            <button type="button" className="sales-primary" disabled={creating} onClick={() => void create()}>{creating ? 'Legt an …' : 'Als Stammkunde anlegen'}</button>
          </div>
          {createError ? <p className="sales-inline-error">{createError}</p> : null}
        </div>
      )}
      <input type="hidden" name="customerId" value={picked?.id ?? ''} readOnly />
      <input type="hidden" name="customerNumber" value={picked?.number ?? ''} readOnly />
      <input type="hidden" name="customerName" value={picked?.name ?? ''} readOnly />
    </div>
  );
}

function OpportunityDialog({
  record,
  tenantId,
  customersGateway,
  busy,
  error,
  onCancel,
  onSave,
}: {
  readonly record: SalesOpportunityRecord | 'new';
  readonly tenantId: string;
  readonly customersGateway: CustomersGateway;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSave: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const current = record === 'new' ? null : record;
  return (
    <div className="sales-modal" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && confirmDiscard()) onCancel();
    }}>
      <form className="sales-modal__panel" onSubmit={onSave}>
        <span className="sales-eyebrow">Pipeline</span>
        <h2>{current ? 'Verkaufschance bearbeiten' : 'Verkaufschance anlegen'}</h2>
        <p>Auch telefonische Anfragen werden sofort nachvollziehbar erfasst.</p>
        <div className="sales-form-grid">
          <CustomerPicker tenantId={tenantId} gateway={customersGateway} initialId={current?.customerId ?? ''} initialNumber={current?.customerNumber ?? ''} initialName={current?.customerName ?? ''} />
          <label>Interessent / Firma (ohne Stammkunde)<input name="prospectName" defaultValue={current?.prospectName ?? ''} /></label>
          <label className="is-wide">Bezeichnung<input name="title" required defaultValue={current?.title ?? ''} placeholder="z. B. Neuer Elektrostapler für Halle 2" /></label>
          <label>
            Quelle
            <SearchSelect name="source" defaultValue={current?.source ?? 'phone'} options={Object.entries(sourceLabels).map(([value, label]) => ({ value, label }))} />
          </label>
          <label>
            Status
            <SearchSelect name="stage" defaultValue={current?.stage ?? 'new'} options={Object.entries(stageLabels)
              .filter(([value]) => !['won', 'lost'].includes(value))
              .map(([value, label]) => ({ value, label }))} />
          </label>
          <label>Ansprechpartner<input name="contactName" defaultValue={current?.contactName ?? ''} /></label>
          <label>Telefon<input name="phone" defaultValue={current?.phone ?? ''} /></label>
          <label>E-Mail<input name="email" type="email" defaultValue={current?.email ?? ''} /></label>
          <label>Erwarteter Nettowert<input name="expectedValue" type="number" min="0" step="0.01" defaultValue={current?.expectedValue ?? 0} /></label>
          <label>Wahrscheinlichkeit %<input name="probability" type="number" min="0" max="100" defaultValue={current?.probability ?? 20} /></label>
          <label>Nächste Aktion am<input name="nextActionDate" type="date" defaultValue={current?.nextActionDate ?? ''} /></label>
          <label className="is-wide">Nächste Aktion<input name="nextAction" defaultValue={current?.nextAction ?? ''} placeholder="z. B. telefonisch nachfassen" /></label>
          <label className="is-wide">Notizen<textarea name="notes" rows={4} defaultValue={current?.notes ?? ''} /></label>
        </div>
        {error ? <div className="sales-modal__error" role="alert">{error}</div> : null}
        <div className="sales-modal__actions">
          <button type="button" className="sales-secondary" onClick={onCancel}>Abbrechen</button>
          <button type="submit" className="sales-primary" disabled={busy}>{busy ? 'Speichert …' : 'Speichern'}</button>
        </div>
      </form>
    </div>
  );
}

function OfferDialog({
  record,
  mode,
  opportunities,
  selectedOpportunity,
  tenantId,
  customersGateway,
  machines,
  items,
  warengruppen,
  positions,
  canViewMargin,
  busy,
  error,
  onPositionsChange,
  onCancel,
  onSave,
}: {
  readonly record: SalesOfferRecord | 'new';
  readonly mode: 'offer' | 'direct';
  readonly opportunities: readonly SalesOpportunityRecord[];
  readonly selectedOpportunity: SalesOpportunityRecord | null;
  readonly tenantId: string;
  readonly customersGateway: CustomersGateway;
  readonly machines: readonly SalesMachineOption[];
  readonly items: readonly SalesItemOption[];
  readonly warengruppen: readonly { readonly id: string; readonly name: string }[];
  readonly positions: readonly PositionDraft[];
  readonly canViewMargin: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onPositionsChange: (positions: PositionDraft[]) => void;
  readonly onCancel: () => void;
  readonly onSave: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const current = record === 'new' ? null : record;

  // Titel-Vorbelegung: solange der Nutzer den Titel nicht selbst angefasst hat, schlägt das Formular
  // "Angebot <Kunde> – <erste Maschine>" vor und aktualisiert das live mit Kunde/Maschine. Die
  // endgültige Angebotsnummer vergibt erst der Server, daher steht sie (noch) nicht im Titel.
  const [pickedCustomer, setPickedCustomer] = useState<PickedCustomer | null>(
    current?.customerNumber || selectedOpportunity?.customerNumber
      ? {
          id: current?.customerId ?? selectedOpportunity?.customerId ?? '',
          number: current?.customerNumber ?? selectedOpportunity?.customerNumber ?? '',
          name: current?.customerName ?? selectedOpportunity?.customerName ?? '',
        }
      : null,
  );
  const [title, setTitle] = useState(current?.title ?? '');
  const [titleTouched, setTitleTouched] = useState(Boolean(current?.title));
  const firstMachineLabel = positions.find((position) => position.kind === 'machine' && position.description.trim())?.description.trim() ?? '';
  const suggestedTitle = ['Angebot', pickedCustomer?.name, firstMachineLabel && `– ${firstMachineLabel}`].filter(Boolean).join(' ');
  const titleValue = titleTouched ? title : suggestedTitle;

  const patchPosition = (id: string, patch: Partial<PositionDraft>) => {
    onPositionsChange(positions.map((position) => position.id === id ? { ...position, ...patch } : position));
  };

  const selectSource = (position: PositionDraft, sourceId: string) => {
    if (position.kind === 'machine') {
      const machine = machines.find((item) => item.id === sourceId);
      if (machine) {
        patchPosition(position.id, {
          sourceId,
          description: `${machine.manufacturer} ${machine.model}`,
          unit: 'Stück',
          unitPrice: String(machine.salesPrice),
          purchasePrice: String(machine.purchasePrice),
        });
      }
      return;
    }
    const item = items.find((candidate) => candidate.id === sourceId && candidate.kind === position.kind);
    if (item) {
      patchPosition(position.id, {
        sourceId,
        description: item.name,
        unit: item.unit,
        unitPrice: String(item.salesPrice),
        purchasePrice: String(item.purchasePrice),
        vatRate: String(item.vatRate),
      });
    }
  };

  return (
    <div className="sales-modal" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && confirmDiscard()) onCancel();
    }}>
      <form className="sales-modal__panel is-wide" onSubmit={onSave}>
        <span className="sales-eyebrow">{mode === 'direct' ? 'Direktverkauf' : 'Angebot'}</span>
        <h2>{mode === 'direct' ? 'Direktverkauf erfassen' : current ? 'Angebot bearbeiten' : 'Angebot erfassen'}</h2>
        <p>{mode === 'direct'
          ? 'Ohne Angebot – direkt als Verkaufsauftrag mit Rechnungsentwurf an FORQ Finance.'
          : 'Telefonische Angebote sind genauso möglich wie ein späteres Dokument.'}</p>
        <div className="sales-form-grid">
          {mode === 'offer' ? (
            <label>
              Verkaufschance
              <SearchSelect name="opportunityId" defaultValue={current?.opportunityId ?? selectedOpportunity?.id ?? ''} options={[
                { value: '', label: 'Ohne Verkaufschance' },
                ...opportunities.filter((item) => !['won', 'lost'].includes(item.stage)).map((item) => ({ value: item.id, label: `${item.number} · ${item.title}` })),
              ]} />
            </label>
          ) : null}
          <CustomerPicker tenantId={tenantId} gateway={customersGateway} initialId={current?.customerId ?? selectedOpportunity?.customerId ?? ''} initialNumber={current?.customerNumber ?? selectedOpportunity?.customerNumber ?? ''} initialName={current?.customerName ?? selectedOpportunity?.customerName ?? ''} onPick={setPickedCustomer} />
          <label>Interessent / Firma (ohne Stammkunde)<input name="prospectName" defaultValue={current?.prospectName ?? selectedOpportunity?.prospectName ?? ''} /></label>
          <label className="is-wide">Titel<input name="title" required value={titleValue} onChange={(event) => { setTitle(event.target.value); setTitleTouched(true); }} /></label>
          <label>
            Kanal
            <SearchSelect name="channel" defaultValue={current?.channel ?? 'phone'} options={Object.entries(channelLabels).map(([value, label]) => ({ value, label }))} />
          </label>
          {mode === 'offer' ? (
            <label>
              Status
              <SearchSelect name="status" defaultValue={current?.status ?? 'presented'} options={[
                { value: 'draft', label: 'Entwurf' },
                { value: 'presented', label: 'Vorgestellt' },
                { value: 'follow-up', label: 'Nachfassen' },
                { value: 'expired', label: 'Abgelaufen' },
              ]} />
            </label>
          ) : null}
          <label>Gültig bis<input name="validUntil" type="date" defaultValue={current?.validUntil ?? ''} /></label>
          <label className="is-wide">Notizen<textarea name="notes" rows={2} defaultValue={current?.notes ?? ''} /></label>
        </div>

        <div className="sales-position-editor-heading">
          <div><span className="sales-eyebrow">Positionen</span><h3>Leistungen und Maschinen</h3></div>
          <button type="button" className="sales-secondary" onClick={() => onPositionsChange([...positions, emptyPosition()])}><Plus size={15} /> Position</button>
        </div>

        <div className="sales-position-editor">
          {positions.map((position, index) => {
            const sourceOptions = position.kind === 'machine'
              ? machines
              : items.filter((item) => item.kind === position.kind);
            return (
              <article key={position.id}>
                <div className="sales-position-editor__top">
                  <strong>Position {index + 1}</strong>
                  <button type="button" onClick={() => onPositionsChange(positions.filter((item) => item.id !== position.id))} disabled={positions.length === 1}><XCircle size={15} /></button>
                </div>
                <div className="sales-position-grid">
                  <label>
                    Art
                    <SearchSelect value={position.kind} onChange={(next) => patchPosition(position.id, {
                      kind: next as SalesPositionKind,
                      sourceId: '',
                      description: '',
                      unitPrice: '0',
                      purchasePrice: '0',
                    })} options={Object.entries(positionKindLabels).map(([value, label]) => ({ value, label }))} />
                  </label>
                  {position.kind !== 'text' ? (
                    <label>
                      Quelle
                      <SearchSelect value={position.sourceId} onChange={(next) => selectSource(position, next)} options={[
                        { value: '', label: 'Auswählen' },
                        ...sourceOptions.map((source) => ({
                          value: source.id,
                          label: `${source.number} · ${'name' in source ? source.name : `${source.manufacturer} ${source.model}`}`,
                          ...('attributes' in source ? { hint: machineSpecHint(source) } : {}),
                        })),
                      ]} />
                    </label>
                  ) : null}
                  <label className="is-wide">Beschreibung<input value={position.description} onChange={(event) => patchPosition(position.id, { description: event.target.value })} required /></label>
                  <label>Menge<input value={position.quantity} onChange={(event) => patchPosition(position.id, { quantity: event.target.value })} type="number" min="0.001" step="0.001" /></label>
                  <label>Einheit<input value={position.unit} onChange={(event) => patchPosition(position.id, { unit: event.target.value })} /></label>
                  <label>Verkaufspreis<input value={position.unitPrice} onChange={(event) => patchPosition(position.id, { unitPrice: event.target.value })} type="number" min="0" step="0.01" /></label>
                  {canViewMargin ? <label>Einkaufspreis<input value={position.purchasePrice} onChange={(event) => patchPosition(position.id, { purchasePrice: event.target.value })} type="number" min="0" step="0.01" /></label> : null}
                  <label>Rabatt %<input value={position.discountPercent} onChange={(event) => patchPosition(position.id, { discountPercent: event.target.value })} type="number" min="0" max="100" step="0.01" /></label>
                  <label>USt. %<input value={position.vatRate} onChange={(event) => patchPosition(position.id, { vatRate: event.target.value })} type="number" min="0" max="100" step="0.01" /></label>
                  <label>Warengruppe<SearchSelect value={position.warengruppeId} onChange={(next) => patchPosition(position.id, { warengruppeId: next })} options={[{ value: '', label: '— (aus Stamm/Art)' }, ...warengruppen.map((entry) => ({ value: entry.id, label: entry.name }))]} /></label>
                </div>
              </article>
            );
          })}
        </div>

        {error ? <div className="sales-modal__error" role="alert">{error}</div> : null}
        <div className="sales-modal__actions">
          <button type="button" className="sales-secondary" onClick={onCancel}>Abbrechen</button>
          <button type="submit" className="sales-primary" disabled={busy}>{busy ? 'Speichert …' : mode === 'direct' ? 'Verkaufen & abrechnen' : 'Angebot speichern'}</button>
        </div>
      </form>
    </div>
  );
}

interface PackageSaveInput {
  readonly customerId: string;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly title: string;
  readonly packagePrice: number | null;
  readonly lines: readonly { readonly machineId: string; readonly description: string; readonly unitPrice: number; readonly purchasePrice: number; readonly vatRate: number }[];
}

function PackageDialog({ machines, tenantId, customersGateway, busy, error, onCancel, onSave }: {
  readonly machines: readonly SalesMachineOption[];
  readonly tenantId: string;
  readonly customersGateway: CustomersGateway;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSave: (input: PackageSaveInput) => void;
}) {
  const [picks, setPicks] = useState<readonly string[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [packagePrice, setPackagePrice] = useState('');
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);

  function toggle(machine: SalesMachineOption): void {
    setPicks((current) => current.includes(machine.id) ? current.filter((id) => id !== machine.id) : [...current, machine.id]);
    setPrices((current) => current[machine.id] ? current : { ...current, [machine.id]: String(machine.salesPrice) });
  }

  const selected = picks
    .map((id) => machines.find((machine) => machine.id === id))
    .filter((machine): machine is SalesMachineOption => Boolean(machine));
  const priceOf = (id: string): number => Number((prices[id] ?? '').replace(',', '.')) || 0;
  const sum = selected.reduce((total, machine) => total + priceOf(machine.id), 0);
  const pkg = packagePrice.trim() ? (Number(packagePrice.replace(',', '.')) || 0) : null;
  const suggestedTitle = ['Paketangebot', customer?.name].filter(Boolean).join(' ');
  const titleValue = titleTouched ? title : suggestedTitle;

  function submit(): void {
    if (selected.length === 0) return;
    onSave({
      customerId: customer?.id ?? '',
      customerNumber: customer?.number ?? '',
      customerName: customer?.name ?? '',
      title: titleValue,
      packagePrice: pkg,
      lines: selected.map((machine) => ({
        machineId: machine.id,
        description: `${machine.manufacturer} ${machine.model}`,
        unitPrice: priceOf(machine.id),
        purchasePrice: machine.purchasePrice,
        vatRate: 19,
      })),
    });
  }

  return (
    <div className="sales-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) onCancel(); }}>
      <div className="sales-modal__panel is-wide">
        <span className="sales-eyebrow">Paketangebot</span>
        <h2>Paketangebot erstellen</h2>
        <p>Mehrere Maschinen anklicken, Preise anpassen, optional einen gemeinsamen Paketpreis setzen.</p>
        <div className="sales-form-grid">
          <CustomerPicker tenantId={tenantId} gateway={customersGateway} initialId="" initialNumber="" initialName="" onPick={setCustomer} />
          <label className="is-wide">Titel<input value={titleValue} onChange={(event) => { setTitle(event.target.value); setTitleTouched(true); }} required /></label>
        </div>
        <div className="sales-package-list">
          {machines.map((machine) => {
            const on = picks.includes(machine.id);
            return (
              <button type="button" key={machine.id} className={`sales-package-row${on ? ' is-on' : ''}`} onClick={() => toggle(machine)}>
                <span className="sales-package-check">{on ? '✓' : ''}</span>
                <span className="forq-stack"><strong>{machine.number} · {machine.manufacturer} {machine.model}</strong><small>{machineSpecHint(machine)}</small></span>
                <span className="sales-package-price">{money(machine.salesPrice)}</span>
              </button>
            );
          })}
          {machines.length === 0 ? <p className="sales-empty">Keine verkaufbaren Maschinen im Bestand.</p> : null}
        </div>
        {selected.length > 0 ? (
          <div className="sales-package-selected">
            <h3 className="sales-subheading">Ausgewählt ({selected.length})</h3>
            {selected.map((machine) => (
              <div className="sales-package-selected__row" key={machine.id}>
                <span>{machine.number} · {machine.manufacturer} {machine.model}</span>
                <label>Einzelpreis (netto)<input type="number" min="0" step="0.01" value={prices[machine.id] ?? ''} onChange={(event) => setPrices((current) => ({ ...current, [machine.id]: event.target.value }))} /></label>
              </div>
            ))}
            <div className="sales-package-totals">
              <span>Einzelsumme <strong>{money(sum)}</strong></span>
              <label>Paketpreis (netto, optional)<input type="number" min="0" step="0.01" value={packagePrice} onChange={(event) => setPackagePrice(event.target.value)} placeholder={String(Math.round(sum))} /></label>
              <span>Angebotswert <strong>{money(pkg ?? sum)}</strong></span>
            </div>
          </div>
        ) : null}
        {error ? <div className="sales-modal__error" role="alert">{error}</div> : null}
        <div className="sales-modal__actions">
          <button type="button" className="sales-secondary" onClick={onCancel}>Abbrechen</button>
          <button type="button" className="sales-primary" disabled={busy || selected.length === 0} onClick={submit}>{busy ? 'Speichert …' : 'Paketangebot erstellen'}</button>
        </div>
      </div>
    </div>
  );
}

interface SaleConfirmInput {
  readonly prices: Readonly<Record<string, number>>;
  readonly delivery: SalesOrderDelivery;
}

// Verkaufsmeldung beim Gewinnen: alle Verkaufsdaten final prüfen/eintragen, dann an Finance/Service.
function SaleConfirmDialog({ offer, customerAddress, busy, error, onCancel, onConfirm }: {
  readonly offer: SalesOfferRecord;
  readonly customerAddress: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: (input: SaleConfirmInput) => void;
}) {
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(offer.positions.map((position) => [position.id, String(position.unitPrice)])));
  const [address, setAddress] = useState(customerAddress);
  const [transport, setTransport] = useState('0');
  const [method, setMethod] = useState('');
  const [readyDate, setReadyDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [conditions, setConditions] = useState('');

  const priceOf = (position: SalesOfferPositionRecord): number => {
    const value = Number((prices[position.id] ?? '').replace(',', '.'));
    return Number.isFinite(value) ? value : position.unitPrice;
  };
  const positionsNet = offer.positions.reduce((sum, position) => sum + priceOf(position) * position.quantity * (1 - position.discountPercent / 100), 0);
  const transportCost = Number(transport.replace(',', '.')) || 0;

  return (
    <div className="sales-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
      <form className="sales-modal__panel is-wide" onSubmit={(event) => {
        event.preventDefault();
        onConfirm({
          prices: Object.fromEntries(offer.positions.map((position) => [position.id, priceOf(position)])),
          delivery: { address: address.trim(), readyDate, deliveryDate, method: method.trim(), cost: transportCost, priceNote: conditions.trim() },
        });
      }}>
        <span className="sales-eyebrow">Verkaufsmeldung · {offer.number}</span>
        <h2>Verkauf abschließen</h2>
        <p>Alle Verkaufsdaten prüfen und final eintragen. Mit „Bestätigen" wird der Auftrag erzeugt und an Finance und Service übergeben.</p>
        <div className="sales-form-grid">
          <div className="is-wide"><span className="sales-field-label">Kunde</span><strong>{offer.customerName || offer.prospectName || '–'}</strong></div>
          <label className="is-wide">Lieferadresse<textarea rows={3} value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Firma, Straße, PLZ Ort" /></label>
        </div>
        <h3 className="sales-subheading">Positionen &amp; Preise</h3>
        <div className="sales-confirm-positions">
          {offer.positions.filter((position) => position.kind !== 'text').map((position) => (
            <div className="sales-confirm-pos" key={position.id}>
              <span className="forq-stack"><strong>{position.description}</strong><small>{position.quantity} {position.unit} · {positionKindLabels[position.kind]}</small></span>
              <label>Einzelpreis (netto)<input type="number" min="0" step="0.01" value={prices[position.id] ?? ''} onChange={(event) => setPrices((current) => ({ ...current, [position.id]: event.target.value }))} /></label>
            </div>
          ))}
        </div>
        <div className="sales-form-grid">
          <label>Transportkosten (netto)<input type="number" min="0" step="0.01" value={transport} onChange={(event) => setTransport(event.target.value)} /></label>
          <label>Lieferart<SearchSelect value={method} onChange={setMethod} options={[
            { value: '', label: '—' },
            { value: 'Anlieferung durch FTW', label: 'Anlieferung durch FTW' },
            { value: 'Selbstabholung', label: 'Selbstabholung' },
            { value: 'Spedition', label: 'Spedition' },
          ]} /></label>
          <label>Fertig bis<input type="date" value={readyDate} onChange={(event) => setReadyDate(event.target.value)} /></label>
          <label>Liefertermin (beim Kunden)<input type="date" value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} /></label>
          <label className="is-wide">Bedingungen / Hinweise<textarea rows={2} value={conditions} onChange={(event) => setConditions(event.target.value)} placeholder="z. B. Zahlungsziel, Garantie, Sonderkonditionen" /></label>
        </div>
        <div className="sales-confirm-total"><span>Verkaufswert netto (inkl. Transport)</span><strong>{money(positionsNet + transportCost)}</strong></div>
        {error ? <div className="sales-modal__error" role="alert">{error}</div> : null}
        <div className="sales-modal__actions">
          <button type="button" className="sales-secondary" onClick={onCancel}>Abbrechen</button>
          <button type="submit" className="sales-primary" disabled={busy}>{busy ? 'Übergibt …' : 'Bestätigen & übergeben'}</button>
        </div>
      </form>
    </div>
  );
}

function DeliveryDialog({ order, busy, error, onCancel, onSave }: {
  readonly order: SalesOrderRecord;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSave: (order: SalesOrderRecord, delivery: SalesOrderDelivery) => void;
}) {
  const existing = order.delivery;
  const [address, setAddress] = useState(existing?.address ?? '');
  const [readyDate, setReadyDate] = useState(existing?.readyDate ?? '');
  const [deliveryDate, setDeliveryDate] = useState(existing?.deliveryDate ?? '');
  const [method, setMethod] = useState(existing?.method ?? '');
  const [cost, setCost] = useState(existing ? String(existing.cost) : '0');
  const [priceNote, setPriceNote] = useState(existing?.priceNote ?? '');

  return (
    <div className="sales-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
      <form className="sales-modal__panel" onSubmit={(event) => {
        event.preventDefault();
        onSave(order, {
          address: address.trim(),
          readyDate,
          deliveryDate,
          method: method.trim(),
          cost: Number(cost.replace(',', '.')) || 0,
          priceNote: priceNote.trim(),
        });
      }}>
        <span className="sales-eyebrow">Verkaufsmeldung · {order.number}</span>
        <h2>Verkaufsmeldung erfassen</h2>
        <p>Liefer- und Abrechnungsdetails für Finance (Rechnung) und Service/Werkstatt (Auslieferung).</p>
        <div className="sales-form-grid">
          <label className="is-wide">Lieferadresse<textarea rows={3} value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Firma, Straße, PLZ Ort" /></label>
          <label>Fertig bis<input type="date" value={readyDate} onChange={(event) => setReadyDate(event.target.value)} /></label>
          <label>Liefertermin<input type="date" value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} /></label>
          <label>Lieferart<SearchSelect value={method} onChange={setMethod} options={[
            { value: '', label: '—' },
            { value: 'Anlieferung durch FTW', label: 'Anlieferung durch FTW' },
            { value: 'Selbstabholung', label: 'Selbstabholung' },
            { value: 'Spedition', label: 'Spedition' },
          ]} /></label>
          <label>Lieferkosten (netto)<input type="number" min="0" step="0.01" value={cost} onChange={(event) => setCost(event.target.value)} /></label>
          <label className="is-wide">Preis-/Kurs-Hinweis für Finance<textarea rows={2} value={priceNote} onChange={(event) => setPriceNote(event.target.value)} placeholder="z. B. wie Angebot, Zahlungsziel, Sonderkonditionen" /></label>
        </div>
        {error ? <div className="sales-modal__error" role="alert">{error}</div> : null}
        <div className="sales-modal__actions">
          <button type="button" className="sales-secondary" onClick={onCancel}>Abbrechen</button>
          <button type="submit" className="sales-primary" disabled={busy}>{busy ? 'Speichert …' : 'Verkaufsmeldung speichern'}</button>
        </div>
      </form>
    </div>
  );
}

export function SalesPage({ tenant, mode, permissions, enabledFeatureIds, gateway, deletionGateway, customersGateway, financeGateway, companyGateway, machinesGateway, masterdataGateway, mastersGateway, pendingAction, onActionConsumed }: SalesPageProps) {
  const availableSections = useMemo(
    () => sections.filter((section) => enabledFeatureIds.includes(section.featureId)),
    [enabledFeatureIds],
  );
  const [activeSection, setActiveSection] = useState<SalesSection>(availableSections[0]?.id ?? 'overview');
  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [opportunities, setOpportunities] = useState<readonly SalesOpportunityRecord[]>([]);
  const [offers, setOffers] = useState<readonly SalesOfferRecord[]>([]);
  const [orders, setOrders] = useState<readonly SalesOrderRecord[]>([]);
  const [handoffs, setHandoffs] = useState<readonly SalesHandoffRecord[]>([]);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState('');
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState<SalesOpportunityStage | 'all'>('all');
  const [offerStatus, setOfferStatus] = useState<SalesOfferStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [opportunityEditor, setOpportunityEditor] = useState<SalesOpportunityRecord | 'new' | null>(null);
  const [offerEditor, setOfferEditor] = useState<SalesOfferRecord | 'new' | null>(null);
  const [packageEditor, setPackageEditor] = useState<'new' | null>(null);
  const [deliveryEditor, setDeliveryEditor] = useState<SalesOrderRecord | null>(null);
  const [saleConfirm, setSaleConfirm] = useState<{ readonly offer: SalesOfferRecord; readonly address: string } | null>(null);
  const [directSale, setDirectSale] = useState(false);
  const [machines, setMachines] = useState<readonly SalesMachineOption[]>([]);
  const [items, setItems] = useState<readonly SalesItemOption[]>([]);
  const [warengruppenOptions, setWarengruppenOptions] = useState<readonly { readonly id: string; readonly name: string }[]>([]);
  const [positions, setPositions] = useState<PositionDraft[]>([emptyPosition()]);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [documentHtml, setDocumentHtml] = useState<string | null>(null);

  // Warengruppen-Stamm einmalig laden — für den Positions-Picker UND die Namensauflösung beim Abrechnen.
  useEffect(() => {
    let active = true;
    mastersGateway.listWarengruppen(tenant.id).then((list) => { if (active) setWarengruppenOptions(list); }).catch(() => { if (active) setWarengruppenOptions([]); });
    return () => { active = false; };
  }, [mastersGateway, tenant.id]);

  const selectedOpportunity = opportunities.find((item) => item.id === selectedOpportunityId) ?? opportunities[0] ?? null;
  const selectedOffer = offers.find((item) => item.id === selectedOfferId) ?? offers[0] ?? null;
  const selectedOrder = orders.find((item) => item.id === selectedOrderId) ?? orders[0] ?? null;
  const canManageOpportunities = permissions.has('sales.opportunities.manage');
  const canManageOffers = permissions.has('sales.offers.manage');
  const canDecide = permissions.has('sales.decisions.manage');
  const canViewMargin = permissions.has('sales.margin.view');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextOverview, opportunityPage, offerPage, orderPage, nextHandoffs] = await Promise.all([
        gateway.getOverview(tenant.id),
        gateway.searchOpportunities({ tenantId: tenant.id, query, stage, pageSize: 100 }),
        gateway.searchOffers({ tenantId: tenant.id, query, status: offerStatus, pageSize: 100 }),
        gateway.searchOrders({ tenantId: tenant.id, query, pageSize: 100 }),
        gateway.listHandoffs(tenant.id, 100),
      ]);
      setOverview(nextOverview);
      setOpportunities(opportunityPage.items);
      setOffers(offerPage.items);
      setOrders(orderPage.items);
      setHandoffs(nextHandoffs);
      setSelectedOpportunityId((current) => opportunityPage.items.some((item) => item.id === current) ? current : opportunityPage.items[0]?.id ?? '');
      setSelectedOfferId((current) => offerPage.items.some((item) => item.id === current) ? current : offerPage.items[0]?.id ?? '');
      setSelectedOrderId((current) => orderPage.items.some((item) => item.id === current) ? current : orderPage.items[0]?.id ?? '');
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, offerStatus, query, stage, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  // FORQ-eigenes Rechtsklick-Menü auf Angebots-Zeilen.
  const canDeleteOffer = permissions.has('sales.offers.delete');
  const { open: openOfferMenu, menu: offerContextMenu } = useContextMenu();
  const { requestDelete: requestOfferDelete, deleteDialog: offerDeleteDialog } = useRecordDeleteFlow(deletionGateway, tenant.id);
  const offerMenuItems = useCallback((offer: SalesOfferRecord): ContextMenuItem[] => [
    { label: 'Öffnen', icon: <FileText size={15} />, onSelect: () => setSelectedOfferId(offer.id) },
    {
      label: 'Löschen', icon: <Trash2 size={15} />, danger: true, disabled: !canDeleteOffer, separatorBefore: true,
      onSelect: () => requestOfferDelete({
        entity: 'offer', id: offer.id, noun: 'Angebot', label: offer.number,
        onDone: (outcome) => {
          setNotice(describeDeleteOutcome(outcome, 'Angebot', offer.number));
          setSelectedOfferId((current) => (current === offer.id ? '' : current));
          void load();
        },
      }),
    },
  ], [canDeleteOffer, requestOfferDelete, load]);

  useEffect(() => {
    let active = true;
    companyGateway.loadProfile(tenant.id)
      .then((profile) => { if (active) setCompany(profile); })
      .catch(() => { if (active) setCompany(emptyCompanyProfile); });
    return () => { active = false; };
  }, [companyGateway, tenant.id]);
  useEffect(() => {
    if (!availableSections.some((section) => section.id === activeSection)) {
      setActiveSection(availableSections[0]?.id ?? 'overview');
    }
  }, [activeSection, availableSections]);

  const openOpportunityEditor = useCallback((record: SalesOpportunityRecord | 'new') => {
    setError(null);
    setOpportunityEditor(record);
  }, []);

  const openOfferEditor = useCallback(async (record: SalesOfferRecord | 'new', opportunity?: SalesOpportunityRecord | null, direct = false) => {
    setBusy(true);
    setError(null);
    setDirectSale(direct);
    try {
      const [nextMachines, nextItems] = await Promise.all([
        gateway.searchMachineOptions(tenant.id),
        gateway.searchItemOptions(tenant.id),
      ]);
      setMachines(nextMachines);
      setItems(nextItems);
      if (record === 'new') {
        setPositions([emptyPosition()]);
        if (opportunity) setSelectedOpportunityId(opportunity.id);
      } else {
        setPositions(record.positions.map((position) => ({
          id: position.id,
          kind: position.kind,
          sourceId: position.sourceId ?? '',
          description: position.description,
          quantity: String(position.quantity),
          unit: position.unit,
          unitPrice: String(position.unitPrice),
          purchasePrice: String(position.purchasePrice),
          discountPercent: String(position.discountPercent),
          vatRate: String(position.vatRate),
          warengruppeId: position.warengruppeId ?? '',
        })));
      }
      setOfferEditor(record);
    } catch (editorError) {
      setError(messageOf(editorError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id]);

  const openPackageEditor = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setMachines(await gateway.searchMachineOptions(tenant.id));
      setPackageEditor('new');
    } catch (editorError) {
      setError(messageOf(editorError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id]);

  // Paketangebot speichern: mehrere Maschinen -> ein Angebot. Ist ein Paketpreis gesetzt, werden die
  // Einzelpreise proportional darauf skaliert (die Positionen summieren sich dann auf den Paketpreis).
  const savePackage = useCallback(async (input: {
    readonly customerId: string; readonly customerNumber: string; readonly customerName: string;
    readonly title: string; readonly packagePrice: number | null;
    readonly lines: readonly { readonly machineId: string; readonly description: string; readonly unitPrice: number; readonly purchasePrice: number; readonly vatRate: number }[];
  }) => {
    setBusy(true);
    setError(null);
    try {
      const sum = input.lines.reduce((total, line) => total + line.unitPrice, 0);
      const factor = input.packagePrice != null && sum > 0 ? input.packagePrice / sum : 1;
      const positions: SalesOfferPositionInput[] = input.lines.map((line) => ({
        kind: 'machine',
        sourceId: line.machineId,
        machineId: line.machineId,
        description: line.description,
        quantity: 1,
        unit: 'Stück',
        unitPrice: Math.round(line.unitPrice * factor * 100) / 100,
        purchasePrice: line.purchasePrice,
        vatRate: line.vatRate,
      }));
      const offer = await gateway.saveOffer({
        tenantId: tenant.id,
        customerId: input.customerId || null,
        customerNumber: input.customerNumber,
        customerName: input.customerName,
        title: input.title,
        channel: 'onsite',
        status: 'presented',
        positions,
      });
      setPackageEditor(null);
      await load();
      setSelectedOfferId(offer.id);
      setActiveSection('offers');
    } catch (packageError) {
      setError(messageOf(packageError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id, load]);

  // The Sales-Center shell (header/cockpit) can request an action; open the matching dialog once.
  useEffect(() => {
    if (!pendingAction) return;
    if (pendingAction === 'lead') openOpportunityEditor('new');
    else if (pendingAction === 'direktverkauf') void openOfferEditor('new', null, true);
    else if (pendingAction === 'angebot') void openOfferEditor('new', null);
    else if (pendingAction === 'paket') void openPackageEditor();
    onActionConsumed?.();
  }, [pendingAction, openOpportunityEditor, openOfferEditor, openPackageEditor, onActionConsumed]);

  const saveOpportunity = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await gateway.saveOpportunity({
        tenantId: tenant.id,
        opportunityId: opportunityEditor !== 'new' && opportunityEditor ? opportunityEditor.id : undefined,
        customerId: String(form.get('customerId') ?? '') || null,
        customerNumber: String(form.get('customerNumber') ?? ''),
        customerName: String(form.get('customerName') ?? ''),
        prospectName: String(form.get('prospectName') ?? ''),
        contactName: String(form.get('contactName') ?? ''),
        email: String(form.get('email') ?? ''),
        phone: String(form.get('phone') ?? ''),
        title: String(form.get('title') ?? ''),
        source: String(form.get('source') ?? 'other') as SalesSource,
        stage: String(form.get('stage') ?? 'new') as SalesOpportunityStage,
        expectedValue: parseNumber(form.get('expectedValue')),
        probability: parseNumber(form.get('probability')),
        nextActionDate: String(form.get('nextActionDate') ?? '') || null,
        nextAction: String(form.get('nextAction') ?? ''),
        notes: String(form.get('notes') ?? ''),
      });
      setOpportunityEditor(null);
      await load();
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, opportunityEditor, tenant.id]);

  const saveOffer = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const opportunityId = String(form.get('opportunityId') ?? '') || null;
    const relatedOpportunity = opportunities.find((item) => item.id === opportunityId) ?? null;
    const positionInputs = buildPositionInputs(positions, machines, items);

    setBusy(true);
    setError(null);
    try {
      await gateway.saveOffer({
        tenantId: tenant.id,
        offerId: offerEditor !== 'new' && offerEditor ? offerEditor.id : undefined,
        opportunityId,
        customerId: String(form.get('customerId') ?? '') || relatedOpportunity?.customerId || null,
        customerNumber: String(form.get('customerNumber') ?? '') || relatedOpportunity?.customerNumber || '',
        customerName: String(form.get('customerName') ?? '') || relatedOpportunity?.customerName || '',
        prospectName: String(form.get('prospectName') ?? '') || relatedOpportunity?.prospectName || '',
        title: String(form.get('title') ?? ''),
        channel: String(form.get('channel') ?? 'document') as SalesOfferChannel,
        status: String(form.get('status') ?? 'draft') as Exclude<SalesOfferStatus, 'won' | 'lost'>,
        validUntil: String(form.get('validUntil') ?? '') || null,
        notes: String(form.get('notes') ?? ''),
        positions: positionInputs,
      });
      setOfferEditor(null);
      await load();
      setActiveSection('offers');
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setBusy(false);
    }
  }, [gateway, items, load, machines, offerEditor, opportunities, positions, tenant.id]);

  // Execute the pending Finance handoff of a won/direct sale: create a real invoice draft in FORQ
  // Finance (sourceType 'sales') and flip the handoff to completed with the draft number.
  //
  // Idempotent: existiert bereits eine Rechnung zu diesem Auftrag (z. B. weil ein früherer Versuch
  // die Rechnung anlegte, aber der Handoff-Abschluss danach fehlschlug), wird DIESE verwendet statt
  // eine zweite anzulegen. So strandet kein gewonnener Auftrag mehr und es entsteht keine Doppel-
  // rechnung -- der Wiederholen-Knopf an der Übergabe ist damit gefahrlos.
  const billOrder = useCallback(async (order: SalesOrderRecord) => {
    const financeHandoff = order.handoffs.find((handoff) => handoff.target === 'finance' && handoff.status === 'pending');
    if (!financeHandoff) return;
    const existing = (await financeGateway.listInvoices({ tenantId: tenant.id }))
      .find((invoice) => invoice.sourceType === 'sales' && invoice.sourceNumber === order.number);
    // Warengruppe je Position aus dem Stamm auflösen (Maschine/Artikel/DL), damit die Auswertung die
    // echte Warengruppe statt der groben Positionsart zeigt. Nachrangig: schlägt sie fehl, greift der Fallback.
    const warengruppen = await Promise.all(order.positions.map(async (position) => {
      // Die vom Nutzer an der Position gesetzte Warengruppe hat Vorrang (auch bei freien Positionen ohne Stamm);
      // sonst aus dem Stamm auflösen (Maschine/Artikel/DL).
      if (position.warengruppeId) {
        return { id: position.warengruppeId, name: warengruppenOptions.find((entry) => entry.id === position.warengruppeId)?.name ?? '' };
      }
      try {
        const machineId = position.machineId ?? position.sourceId;
        if (position.kind === 'machine' && machineId) {
          const machine = await machinesGateway.getMachine(tenant.id, machineId);
          return { id: machine.warengruppeId ?? null, name: machine.warengruppeName ?? '' };
        }
        if ((position.kind === 'article' || position.kind === 'service') && position.sourceId) {
          const item = await masterdataGateway.getItem(tenant.id, position.sourceId);
          return { id: item.warengruppeId ?? null, name: item.warengruppeName ?? '' };
        }
      } catch { /* Warengruppe ist nachrangig -- ohne sie greift der kind-Fallback in der Auswertung. */ }
      return null;
    }));
    // Lieferkosten aus der Verkaufsmeldung als eigene Rechnungsposition mit aufnehmen.
    const invoiceLines = [...salesInvoiceLines(order.positions, warengruppen)];
    if (order.delivery && order.delivery.cost > 0) {
      invoiceLines.push({
        kind: 'freight',
        description: order.delivery.method ? `Lieferung – ${order.delivery.method}` : 'Lieferung',
        quantity: 1,
        unit: 'Pauschale',
        unitPrice: order.delivery.cost,
        discountPercent: 0,
        vatRate: 19,
        warengruppeId: null, warengruppeName: '',
      });
    }
    const resultNumber = existing?.proposalNumber ?? (await financeGateway.createInvoice({
      tenantId: tenant.id,
      customerNumber: order.customerNumber,
      customerName: order.customerName,
      sourceType: 'sales',
      sourceNumber: order.number,
      // Leistungsdatum (§ 14 UStG): der gemeldete Liefertag; fehlt er, setzt die Ablage heute.
      serviceDate: order.delivery?.deliveryDate || null,
      lines: invoiceLines,
    })).proposalNumber;
    await gateway.completeHandoff({ tenantId: tenant.id, handoffId: financeHandoff.id, resultNumber });
  }, [financeGateway, gateway, machinesGateway, masterdataGateway, warengruppenOptions, tenant.id]);

  // Gestrandete Finance-Übergabe erneut ausführen (der Auftrag ist schon gewonnen; hier fehlt nur
  // noch die Rechnung). Dank der Idempotenz von billOrder gefahrlos wiederholbar.
  const retryBilling = useCallback(async (order: SalesOrderRecord) => {
    setBusy(true);
    setError(null);
    try {
      await billOrder(order);
      await load();
    } catch (retryError) {
      setError(messageOf(retryError));
    } finally {
      setBusy(false);
    }
  }, [billOrder, load]);

  // Belege aus einem (bereits vorbereiteten) Auftrag rendern -- ohne Gateway-Aufruf, aus der am
  // Logistik-Handoff hinterlegten LS-Nummer.
  // Beleg in einem eigenen Tab öffnen -> Strg+P bzw. der Drucken-Knopf druckt NUR den Beleg (nicht die
  // App-Seite, die sonst abgeschnitten rauskommt). Fallback aufs eingebettete Modal, falls der
  // Popup-Blocker zuschlägt.
  const openDocument = useCallback((html: string) => {
    const win = window.open('about:blank', '_blank');
    if (win && win.document) {
      win.document.open();
      win.document.write(html);
      win.document.close();
    } else {
      setDocumentHtml(html);
    }
  }, []);

  const showLieferschein = useCallback((order: SalesOrderRecord) => {
    const logistics = order.handoffs.find((handoff) => handoff.target === 'logistics');
    openDocument(renderSalesDeliveryHtml({
      documentNumber: logistics?.resultNumber || order.number,
      order,
      company,
      issuedIso: new Date().toISOString(),
    }));
  }, [company, openDocument]);

  const showUvv = useCallback((order: SalesOrderRecord) => {
    const logistics = order.handoffs.find((handoff) => handoff.target === 'logistics');
    openDocument(renderSalesUvvHtml({
      order,
      company,
      issuedIso: new Date().toISOString(),
      deliveryNumber: logistics?.resultNumber || undefined,
    }));
  }, [company, openDocument]);

  // Angebot als druckfertigen FTW-Beleg öffnen -- holt die Maschinendaten (Technik + Foto) je
  // Maschinenposition frisch, damit der Datenblock unter der Position steht (auch bei verkauften Maschinen).
  const printOffer = useCallback(async (offer: SalesOfferRecord) => {
    setBusy(true);
    setError(null);
    try {
      // Empfänger: Name + Anschrift aus dem Kundenstamm (ohne Kundennummer/"intern").
      let recipientLines: string[] = [offer.customerName || offer.prospectName || '–'];
      if (offer.customerId) {
        const customer = await customersGateway.getCustomer(tenant.id, offer.customerId).catch(() => null);
        if (customer) {
          const address = customer.addresses[0];
          const contact = customer.contacts[0];
          recipientLines = [
            customer.displayName || offer.customerName,
            contact ? [contact.salutation, contact.firstName, contact.lastName].filter(Boolean).join(' ') : '',
            address?.street ?? '',
            address ? `${address.postalCode} ${address.city}`.trim() : '',
          ].filter(Boolean);
        }
      }
      const machineIds = [...new Set(offer.positions.filter((position) => position.machineId).map((position) => position.machineId as string))];
      const details = await Promise.all(machineIds.map((id) => gateway.getMachineDetail(tenant.id, id)));
      const machineDetails: Record<string, SalesMachineDetail> = {};
      details.forEach((detail) => { if (detail) machineDetails[detail.id] = detail; });
      openDocument(renderSalesOfferHtml({
        documentNumber: offer.number,
        recipientLines,
        contactName: '',
        validUntil: offer.validUntil,
        positions: offer.positions,
        netTotal: offer.netTotal,
        vatTotal: offer.vatTotal,
        grossTotal: offer.grossTotal,
        company,
        issuedIso: new Date().toISOString(),
        machineDetails,
      }));
    } catch (printError) {
      setError(messageOf(printError));
    } finally {
      setBusy(false);
    }
  }, [gateway, customersGateway, tenant.id, company, openDocument]);

  // Schritt 1 (Verwaltung): Lieferschein + UVV-Prüfbuch vorbereiten (LS-Nummer, Übergabe -> 'accepted');
  // öffnet direkt den Lieferschein. Der Auftrag wartet danach auf die Werkstatt-Durchsicht.
  const prepareDelivery = useCallback(async (order: SalesOrderRecord) => {
    setBusy(true);
    setError(null);
    try {
      const prepared = await gateway.createDelivery({ tenantId: tenant.id, orderId: order.id });
      showLieferschein(prepared);
      await load();
    } catch (deliveryError) {
      setError(messageOf(deliveryError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id, showLieferschein, load]);

  // Schritt 2 (Werkstatt): Auslieferungsdurchsicht bestätigen -> Auftrag 'delivered'.
  const confirmDelivery = useCallback(async (order: SalesOrderRecord) => {
    setBusy(true);
    setError(null);
    try {
      await gateway.confirmDelivery({ tenantId: tenant.id, orderId: order.id });
      await load();
    } catch (confirmError) {
      setError(messageOf(confirmError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id, load]);

  // Verkaufsmeldung speichern (Lieferdetails am Auftrag).
  const saveDelivery = useCallback(async (order: SalesOrderRecord, delivery: SalesOrderDelivery) => {
    setBusy(true);
    setError(null);
    try {
      await gateway.setDeliveryDetails({ tenantId: tenant.id, orderId: order.id, delivery });
      setDeliveryEditor(null);
      await load();
    } catch (deliveryError) {
      setError(messageOf(deliveryError));
    } finally {
      setBusy(false);
    }
  }, [gateway, tenant.id, load]);

  // Gewinnen öffnet die Verkaufsmeldung: Kundenadresse laden und das Fenster mit den Angebotsdaten öffnen.
  const openSaleConfirm = useCallback(async (offer: SalesOfferRecord) => {
    setBusy(true);
    setError(null);
    try {
      let address = '';
      if (offer.customerId) {
        const customer = await customersGateway.getCustomer(tenant.id, offer.customerId).catch(() => null);
        if (customer) {
          const addr = customer.addresses[0];
          address = [customer.displayName, addr?.street, addr ? `${addr.postalCode} ${addr.city}`.trim() : '']
            .filter(Boolean).join('\n');
        }
      }
      setSaleConfirm({ offer, address });
    } catch (confirmError) {
      setError(messageOf(confirmError));
    } finally {
      setBusy(false);
    }
  }, [customersGateway, tenant.id]);

  // Verkaufsmeldung bestätigt: finale Preise ins Angebot übernehmen, gewinnen, Auslieferdetails setzen.
  const confirmSale = useCallback(async (offer: SalesOfferRecord, input: SaleConfirmInput) => {
    setBusy(true);
    setError(null);
    try {
      const pricesChanged = offer.positions.some((position) => input.prices[position.id] !== position.unitPrice);
      if (pricesChanged) {
        await gateway.saveOffer({
          tenantId: tenant.id,
          offerId: offer.id,
          opportunityId: offer.opportunityId,
          customerId: offer.customerId,
          customerNumber: offer.customerNumber,
          customerName: offer.customerName,
          prospectName: offer.prospectName,
          title: offer.title,
          channel: offer.channel,
          status: (offer.status === 'won' || offer.status === 'lost' ? 'presented' : offer.status) as Exclude<SalesOfferStatus, 'won' | 'lost'>,
          validUntil: offer.validUntil,
          notes: offer.notes,
          positions: offer.positions.map((position) => ({
            kind: position.kind,
            sourceId: position.sourceId,
            sourceNumber: position.sourceNumber,
            machineId: position.machineId,
            description: position.description,
            quantity: position.quantity,
            unit: position.unit,
            unitPrice: input.prices[position.id] ?? position.unitPrice,
            purchasePrice: position.purchasePrice,
            discountPercent: position.discountPercent,
            vatRate: position.vatRate,
            warengruppeId: position.warengruppeId,
          })),
        });
      }
      const order = await gateway.decideOffer({ tenantId: tenant.id, offerId: offer.id, decision: 'won', reason: 'Verkaufsmeldung' });
      if (order) await gateway.setDeliveryDetails({ tenantId: tenant.id, orderId: order.id, delivery: input.delivery });
      setSaleConfirm(null);
      await load();
      if (order) {
        setSelectedOrderId(order.id);
        setActiveSection('orders');
      }
    } catch (confirmError) {
      setError(messageOf(confirmError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, tenant.id]);

  const decideOffer = useCallback(async (offer: SalesOfferRecord, decision: 'won' | 'lost') => {
    if (decision === 'won') { await openSaleConfirm(offer); return; }
    const reason = window.prompt('Warum wurde das Angebot verloren?', '');
    if (reason === null) return;
    setBusy(true);
    setError(null);
    try {
      await gateway.decideOffer({ tenantId: tenant.id, offerId: offer.id, decision: 'lost', reason });
      await load();
    } catch (decisionError) {
      setError(messageOf(decisionError));
    } finally {
      setBusy(false);
    }
  }, [openSaleConfirm, gateway, load, tenant.id]);

  // Direktverkauf: no pipeline — one step to a won order + Finance invoice draft (composes the
  // existing saveOffer + decideOffer so the order still has a proper offer artefact behind it).
  const saveDirectSale = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const positionInputs = buildPositionInputs(positions, machines, items);
    setBusy(true);
    setError(null);
    try {
      const offer = await gateway.saveOffer({
        tenantId: tenant.id,
        customerId: String(form.get('customerId') ?? '') || null,
        customerNumber: String(form.get('customerNumber') ?? ''),
        customerName: String(form.get('customerName') ?? ''),
        prospectName: String(form.get('prospectName') ?? ''),
        title: String(form.get('title') ?? ''),
        channel: String(form.get('channel') ?? 'phone') as SalesOfferChannel,
        status: 'presented',
        positions: positionInputs,
      });
      const order = await gateway.decideOffer({ tenantId: tenant.id, offerId: offer.id, decision: 'won', reason: 'Direktverkauf' });
      if (order) await billOrder(order);
      setOfferEditor(null);
      await load();
      if (order) {
        setSelectedOrderId(order.id);
        setActiveSection('orders');
      }
    } catch (saleError) {
      setError(messageOf(saleError));
    } finally {
      setBusy(false);
    }
  }, [billOrder, gateway, items, load, machines, positions, tenant.id]);

  if (availableSections.length === 0) {
    return (
      <EmptyState
        eyebrow="Keine Sales-Rechte"
        title="Sales ist für diesen Mandanten nicht freigegeben."
        description="Die sichtbaren Funktionen werden aus Rollen und Benutzer-Ausnahmen ermittelt."
      />
    );
  }

  return (
    <div className="sales-page">
      <header className="sales-page__header">
        <div>
          <span className="sales-eyebrow">FORQ Sales</span>
          <h1>Von der Anfrage bis zur sicheren Übergabe</h1>
          <p>{tenant.name} · Verkaufschancen, Angebote und Verkaufsaufträge</p>
        </div>
        <div className="sales-header-actions">
          <span className={`sales-mode-badge ${mode === 'demo' ? 'is-demo' : ''}`}>
            {mode === 'demo' ? 'Demo – Änderungen bis zum Neuladen' : 'Supabase verbunden'}
          </span>
          <button type="button" className="sales-secondary" onClick={() => void load()} disabled={loading || busy}>
            <RefreshCw size={15} /> Aktualisieren
          </button>
        </div>
      </header>

      {notice ? <div className="sales-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>Schließen</button></div> : null}
      {error && !opportunityEditor && !offerEditor && !packageEditor && !deliveryEditor && !saleConfirm ? (
        <div className="sales-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>Schließen</button>
        </div>
      ) : null}

      <nav className="sales-nav" aria-label="Sales-Bereiche">
        {availableSections.map(({ id, label, icon: Icon }) => (
          <button type="button" key={id} className={activeSection === id ? 'is-active' : ''} onClick={() => setActiveSection(id)}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>

      {loading ? <div className="sales-loading">Sales-Daten werden geladen …</div> : null}

      {!loading && activeSection === 'overview' && overview ? (
        <section className="sales-overview">
          <div className="sales-metric-grid">
            <article><ListTodo size={18} /><strong>{overview.openOpportunities}</strong><span>Offene Chancen</span><small>{money(overview.pipelineValue)} Pipeline</small></article>
            <article><CircleDollarSign size={18} /><strong>{money(overview.weightedPipelineValue)}</strong><span>Gewichtete Pipeline</span><small>Wert × Wahrscheinlichkeit</small></article>
            <article><FileText size={18} /><strong>{overview.openOffers}</strong><span>Offene Angebote</span><small>{overview.followUpsDue} Nachfassaktionen fällig</small></article>
            <article><Handshake size={18} /><strong>{overview.wonThisMonth}</strong><span>Gewonnen im Monat</span><small>{money(overview.wonValueThisMonth)} netto</small></article>
            <article><Send size={18} /><strong>{overview.pendingHandoffs}</strong><span>Offene Übergaben</span><small>Finance und Logistics</small></article>
          </div>
          <div className="sales-overview__actions">
            {canManageOpportunities ? <button type="button" className="sales-primary" onClick={() => void openOpportunityEditor('new')}><Plus size={16} /> Verkaufschance</button> : null}
            {canManageOffers ? <button type="button" className="sales-secondary" onClick={() => void openOfferEditor('new', selectedOpportunity)}><Plus size={16} /> Angebot erfassen</button> : null}
            {canManageOffers && canDecide ? <button type="button" className="sales-secondary" onClick={() => void openOfferEditor('new', null, true)}><Zap size={16} /> Direktverkauf</button> : null}
            {canManageOffers ? <button type="button" className="sales-secondary" onClick={() => void openPackageEditor()}><Package size={16} /> Paketangebot</button> : null}
          </div>
          <div className="sales-overview__lists">
            <div className="sales-overview__col">
              <div className="sales-overview__col-head"><h3>Aktuelle Angebote</h3><button type="button" className="sales-link" onClick={() => setActiveSection('offers')}>Alle ansehen</button></div>
              {offers.slice(0, 6).map((offer) => (
                <button type="button" key={offer.id} className="sales-overview__row" onClick={() => { setSelectedOfferId(offer.id); setActiveSection('offers'); }}>
                  <span><strong>{offer.number}</strong><em className={`sales-status is-${offer.status}`}>{offerStatusLabels[offer.status]}</em></span>
                  <small>{customerLabel(offer)} · {money(offer.netTotal)}</small>
                </button>
              ))}
              {offers.length === 0 ? <p className="sales-empty">Noch keine Angebote erfasst.</p> : null}
            </div>
            <div className="sales-overview__col">
              <div className="sales-overview__col-head"><h3>Offene Verkaufschancen</h3><button type="button" className="sales-link" onClick={() => setActiveSection('pipeline')}>Alle ansehen</button></div>
              {opportunities.slice(0, 6).map((item) => (
                <button type="button" key={item.id} className="sales-overview__row" onClick={() => { setSelectedOpportunityId(item.id); setActiveSection('pipeline'); }}>
                  <span><strong>{item.number}</strong><em className={`sales-status is-${item.stage}`}>{stageLabels[item.stage]}</em></span>
                  <small>{customerLabel(item)} · {money(item.expectedValue)}</small>
                </button>
              ))}
              {opportunities.length === 0 ? <p className="sales-empty">Keine offenen Verkaufschancen.</p> : null}
            </div>
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'pipeline' ? (
        <section className="sales-workspace">
          <div className="sales-list-panel">
            <div className="sales-toolbar">
              <label><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nummer, Kunde oder Titel" /></label>
              <SearchSelect value={stage} onChange={(next) => setStage(next as SalesOpportunityStage | 'all')} options={[
                { value: 'all', label: 'Alle Stufen' },
                ...Object.entries(stageLabels).map(([value, label]) => ({ value, label })),
              ]} />
              {canManageOpportunities ? <button type="button" className="sales-primary" onClick={() => void openOpportunityEditor('new')}><Plus size={15} /> Chance</button> : null}
            </div>
            <div className="sales-list">
              {opportunities.map((item) => (
                <button type="button" key={item.id} className={item.id === selectedOpportunity?.id ? 'is-active' : ''} onClick={() => setSelectedOpportunityId(item.id)}>
                  <span><strong>{item.number}</strong><em className={`sales-status is-${item.stage}`}>{stageLabels[item.stage]}</em></span>
                  <b>{item.title}</b>
                  <small>{customerLabel(item)} · {money(item.expectedValue)} · {item.probability}%</small>
                </button>
              ))}
              {opportunities.length === 0 ? <p className="sales-empty">Keine Verkaufschancen gefunden.</p> : null}
            </div>
          </div>
          <div className="sales-detail-panel">
            {selectedOpportunity ? (
              <>
                <div className="sales-detail-heading">
                  <div><span className="sales-eyebrow">{selectedOpportunity.number}</span><h2>{selectedOpportunity.title}</h2><p>{customerLabel(selectedOpportunity)}</p></div>
                  <em className={`sales-status is-${selectedOpportunity.stage}`}>{stageLabels[selectedOpportunity.stage]}</em>
                </div>
                <dl className="sales-detail-grid">
                  <div><dt>Quelle</dt><dd>{sourceLabels[selectedOpportunity.source]}</dd></div>
                  <div><dt>Erwarteter Wert</dt><dd>{money(selectedOpportunity.expectedValue)}</dd></div>
                  <div><dt>Wahrscheinlichkeit</dt><dd>{selectedOpportunity.probability}%</dd></div>
                  <div><dt>Gewichteter Wert</dt><dd>{money(selectedOpportunity.weightedValue)}</dd></div>
                  <div><dt>Ansprechpartner</dt><dd>{selectedOpportunity.contactName || '—'}</dd></div>
                  <div><dt>Kontakt</dt><dd>{selectedOpportunity.phone || selectedOpportunity.email || '—'}</dd></div>
                  <div className="is-wide"><dt>Nächste Aktion</dt><dd>{selectedOpportunity.nextAction || '—'}{selectedOpportunity.nextActionDate ? ` · ${selectedOpportunity.nextActionDate}` : ''}</dd></div>
                  <div className="is-wide"><dt>Notizen</dt><dd>{selectedOpportunity.notes || '—'}</dd></div>
                </dl>
                <div className="sales-detail-actions">
                  {canManageOpportunities && !['won', 'lost'].includes(selectedOpportunity.stage) ? <button type="button" className="sales-secondary" onClick={() => void openOpportunityEditor(selectedOpportunity)}>Bearbeiten</button> : null}
                  {canManageOffers && !['won', 'lost'].includes(selectedOpportunity.stage) ? <button type="button" className="sales-primary" onClick={() => void openOfferEditor('new', selectedOpportunity)}><Plus size={15} /> Angebot erfassen</button> : null}
                </div>
              </>
            ) : <p className="sales-empty">Wähle eine Verkaufschance aus.</p>}
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'offers' ? (
        <section className="sales-workspace">
          <div className="sales-list-panel">
            <div className="sales-toolbar">
              <label><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Angebot, Kunde oder Chance" /></label>
              <SearchSelect value={offerStatus} onChange={(next) => setOfferStatus(next as SalesOfferStatus | 'all')} options={[
                { value: 'all', label: 'Alle Status' },
                ...Object.entries(offerStatusLabels).map(([value, label]) => ({ value, label })),
              ]} />
              {canManageOffers ? <button type="button" className="sales-primary" onClick={() => void openOfferEditor('new', selectedOpportunity)}><Plus size={15} /> Angebot</button> : null}
              {canManageOffers && canDecide ? <button type="button" className="sales-secondary" onClick={() => void openOfferEditor('new', null, true)}><Zap size={15} /> Direktverkauf</button> : null}
            </div>
            <div className="sales-list">
              {offers.map((item) => (
                <button type="button" key={item.id} className={item.id === selectedOffer?.id ? 'is-active' : ''} onClick={() => setSelectedOfferId(item.id)} onContextMenu={(event) => openOfferMenu(event, offerMenuItems(item))}>
                  <span><strong>{item.number}</strong><em className={`sales-status is-${item.status}`}>{offerStatusLabels[item.status]}</em></span>
                  <b>{item.title}</b>
                  <small>{customerLabel(item)} · {channelLabels[item.channel]} · {money(item.netTotal)}</small>
                </button>
              ))}
              {offers.length === 0 ? <p className="sales-empty">Keine Angebote gefunden.</p> : null}
            </div>
          </div>
          <div className="sales-detail-panel">
            {selectedOffer ? (
              <>
                <div className="sales-detail-heading">
                  <div><span className="sales-eyebrow">{selectedOffer.number}</span><h2>{selectedOffer.title}</h2><p>{customerLabel(selectedOffer)} · {channelLabels[selectedOffer.channel]}</p></div>
                  <em className={`sales-status is-${selectedOffer.status}`}>{offerStatusLabels[selectedOffer.status]}</em>
                </div>
                <div className="sales-position-table">
                  <table>
                    <thead><tr><th>Pos.</th><th>Beschreibung</th><th>Menge</th><th>Einzelpreis</th><th>Rabatt</th><th>Netto</th>{canViewMargin ? <th>DB</th> : null}</tr></thead>
                    <tbody>
                      {selectedOffer.positions.map((position) => (
                        <tr key={position.id}>
                          <td>{position.positionNumber}</td>
                          <td><strong>{position.description}</strong><small>{positionKindLabels[position.kind]}{position.sourceNumber ? ` · ${position.sourceNumber}` : ''}</small></td>
                          <td>{position.quantity} {position.unit}</td>
                          <td>{money(position.unitPrice)}</td>
                          <td>{position.discountPercent}%</td>
                          <td>{money(position.netAmount)}</td>
                          {canViewMargin ? <td>{money(position.contributionMargin)}</td> : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sales-totals">
                  <span>Netto <strong>{money(selectedOffer.netTotal)}</strong></span>
                  <span>USt. <strong>{money(selectedOffer.vatTotal)}</strong></span>
                  <span>Brutto <strong>{money(selectedOffer.grossTotal)}</strong></span>
                  {canViewMargin ? <span>Deckungsbeitrag <strong>{money(selectedOffer.contributionMargin)}</strong></span> : null}
                </div>
                {selectedOffer.decisionReason ? <p className="sales-decision-note"><strong>Entscheidung:</strong> {selectedOffer.decisionReason}</p> : null}
                <div className="sales-detail-actions">
                  <button type="button" className="sales-secondary" disabled={busy} onClick={() => void printOffer(selectedOffer)}><FileText size={15} /> Angebot drucken</button>
                  {canManageOffers && !['won', 'lost'].includes(selectedOffer.status) ? <button type="button" className="sales-secondary" onClick={() => void openOfferEditor(selectedOffer)}>Bearbeiten</button> : null}
                  {canDecide && !['won', 'lost', 'expired'].includes(selectedOffer.status) ? (
                    <>
                      <button type="button" className="sales-danger" onClick={() => void decideOffer(selectedOffer, 'lost')} disabled={busy}><XCircle size={15} /> Verloren</button>
                      <button type="button" className="sales-success" onClick={() => void decideOffer(selectedOffer, 'won')} disabled={busy}><CheckCircle2 size={15} /> Gewonnen</button>
                    </>
                  ) : null}
                  {canManageOffers ? <RecordDeleteButton className="sales-secondary is-danger" gateway={deletionGateway} tenantId={tenant.id} entity="offer" id={selectedOffer.id} noun="Angebot" label={selectedOffer.number} canDelete={permissions.has('sales.offers.delete')} onDone={(outcome) => { setNotice(describeDeleteOutcome(outcome, 'Angebot', selectedOffer.number)); setSelectedOfferId(''); void load(); }} /> : null}
                </div>
              </>
            ) : <p className="sales-empty">Wähle ein Angebot aus.</p>}
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'orders' ? (
        <section className="sales-workspace">
          <div className="sales-list-panel">
            <div className="sales-toolbar"><label><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Auftrag, Vorgang oder Kunde" /></label></div>
            <div className="sales-list">
              {orders.map((item) => (
                <button type="button" key={item.id} className={item.id === selectedOrder?.id ? 'is-active' : ''} onClick={() => setSelectedOrderId(item.id)}>
                  <span><strong>{item.number}</strong><em className="sales-status is-won">{item.status}</em></span>
                  <b>{item.title}</b><small>{customerLabel(item)} · {money(item.netTotal)}</small>
                </button>
              ))}
              {orders.length === 0 ? <p className="sales-empty">Noch keine gewonnenen Verkaufsaufträge.</p> : null}
            </div>
          </div>
          <div className="sales-detail-panel">
            {selectedOrder ? (
              <>
                <div className="sales-detail-heading"><div><span className="sales-eyebrow">{selectedOrder.number}</span><h2>{selectedOrder.title}</h2><p>{customerLabel(selectedOrder)} · Vorgang {selectedOrder.businessCaseNumber}</p></div><em className="sales-status is-won">{selectedOrder.status}</em></div>
                <dl className="sales-detail-grid">
                  <div><dt>Angebot</dt><dd>{selectedOrder.offerNumber}</dd></div>
                  <div><dt>Netto</dt><dd>{money(selectedOrder.netTotal)}</dd></div>
                  <div><dt>Brutto</dt><dd>{money(selectedOrder.grossTotal)}</dd></div>
                  <div><dt>Übergaben</dt><dd>{selectedOrder.handoffs.length}</dd></div>
                </dl>
                <div className="sales-delivery-block">
                  <div className="sales-delivery-block__head">
                    <h3 className="sales-subheading">Verkaufsmeldung</h3>
                    {canDecide ? <button type="button" className="sales-handoff-reprint" disabled={busy} onClick={() => { setError(null); setDeliveryEditor(selectedOrder); }}>{selectedOrder.delivery ? 'Bearbeiten' : 'Erfassen'}</button> : null}
                  </div>
                  {selectedOrder.delivery ? (
                    <dl className="sales-delivery-grid">
                      {selectedOrder.delivery.address ? <div><dt>Lieferadresse</dt><dd className="is-multiline">{selectedOrder.delivery.address}</dd></div> : null}
                      {selectedOrder.delivery.readyDate ? <div><dt>Fertig bis</dt><dd>{new Date(selectedOrder.delivery.readyDate).toLocaleDateString('de-DE')}</dd></div> : null}
                      {selectedOrder.delivery.deliveryDate ? <div><dt>Liefertermin</dt><dd>{new Date(selectedOrder.delivery.deliveryDate).toLocaleDateString('de-DE')}</dd></div> : null}
                      {selectedOrder.delivery.method ? <div><dt>Lieferart</dt><dd>{selectedOrder.delivery.method}</dd></div> : null}
                      {selectedOrder.delivery.cost > 0 ? <div><dt>Lieferkosten</dt><dd>{money(selectedOrder.delivery.cost)}</dd></div> : null}
                      {selectedOrder.delivery.priceNote ? <div><dt>Preis-Hinweis</dt><dd className="is-multiline">{selectedOrder.delivery.priceNote}</dd></div> : null}
                    </dl>
                  ) : <p className="sales-empty">Noch nicht erfasst — Lieferadresse, Termine, Lieferart und Kosten für Finance und Service.</p>}
                </div>
                <h3 className="sales-subheading">Persistente Übergaben</h3>
                <div className="sales-handoff-list">
                  {selectedOrder.handoffs.map((handoff) => (
                    <article key={handoff.id}>
                      <Send size={16} />
                      <div><strong>{handoff.title}</strong><small>{handoff.target} · {handoff.status}{handoff.resultNumber ? ` · ${handoff.resultNumber}` : ''}</small></div>
                      {handoff.target === 'finance' && handoff.status === 'pending' ? (
                        <button type="button" className="sales-handoff-run" disabled={busy} onClick={() => void retryBilling(selectedOrder)}>Jetzt abrechnen</button>
                      ) : null}
                      {handoff.target === 'logistics' && canDecide ? (
                        <div className="sales-handoff-actions">
                          {handoff.status === 'pending' ? (
                            <button type="button" className="sales-handoff-run" disabled={busy} onClick={() => void prepareDelivery(selectedOrder)}><Truck size={14} /> Lieferschein + UVV erstellen</button>
                          ) : (
                            <>
                              <button type="button" className="sales-handoff-reprint" disabled={busy} onClick={() => showLieferschein(selectedOrder)}>Lieferschein</button>
                              <button type="button" className="sales-handoff-reprint" disabled={busy} onClick={() => showUvv(selectedOrder)}>UVV-Prüfbuch</button>
                              {handoff.status === 'accepted' ? (
                                <button type="button" className="sales-handoff-run" disabled={busy} onClick={() => void confirmDelivery(selectedOrder)}><CheckCircle2 size={14} /> Durchsicht bestätigen</button>
                              ) : null}
                            </>
                          )}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </>
            ) : <p className="sales-empty">Wähle einen Verkaufsauftrag aus.</p>}
          </div>
        </section>
      ) : null}

      {!loading && activeSection === 'handoffs' ? (
        <section className="sales-card">
          <div className="sales-card__heading"><div><span className="sales-eyebrow">Sichere Modulübergaben</span><h2>Finance & Logistics</h2><p>Gewonnene Verkäufe verschwinden nicht mehr zwischen den Modulen.</p></div></div>
          <div className="sales-handoff-table">
            <table>
              <thead><tr><th>Auftrag</th><th>Ziel</th><th>Aufgabe</th><th>Status</th><th>Ergebnis</th><th>Erstellt</th></tr></thead>
              <tbody>
                {handoffs.map((handoff) => (
                  <tr key={handoff.id}><td>{handoff.salesOrderNumber}</td><td>{handoff.target}</td><td>{handoff.title}</td><td><em className={`sales-status is-${handoff.status}`}>{handoff.status}</em></td><td>{handoff.resultNumber || '—'}</td><td>{new Date(handoff.createdAt).toLocaleString('de-DE')}</td></tr>
                ))}
              </tbody>
            </table>
            {handoffs.length === 0 ? <p className="sales-empty">Keine Übergaben vorhanden.</p> : null}
          </div>
        </section>
      ) : null}

      {documentHtml ? (
        <div className="sales-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDocumentHtml(null); }}>
          <div className="sales-modal__panel is-wide sales-doc-modal">
            <div className="sales-doc-modal__bar">
              <span className="sales-eyebrow">Lieferschein</span>
              <button type="button" className="sales-secondary" onClick={() => setDocumentHtml(null)}>Schließen</button>
            </div>
            <iframe className="sales-doc-frame" title="Lieferschein" srcDoc={documentHtml} />
          </div>
        </div>
      ) : null}

      {opportunityEditor ? (
        <OpportunityDialog
          record={opportunityEditor}
          tenantId={tenant.id}
          customersGateway={customersGateway}
          busy={busy}
          error={error}
          onCancel={() => setOpportunityEditor(null)}
          onSave={saveOpportunity}
        />
      ) : null}

      {offerEditor ? (
        <OfferDialog
          record={offerEditor}
          mode={directSale ? 'direct' : 'offer'}
          opportunities={opportunities}
          selectedOpportunity={directSale ? null : selectedOpportunity}
          tenantId={tenant.id}
          customersGateway={customersGateway}
          machines={machines}
          items={items}
          warengruppen={warengruppenOptions}
          positions={positions}
          canViewMargin={canViewMargin}
          busy={busy}
          error={error}
          onPositionsChange={setPositions}
          onCancel={() => setOfferEditor(null)}
          onSave={directSale ? saveDirectSale : saveOffer}
        />
      ) : null}

      {packageEditor ? (
        <PackageDialog
          machines={machines}
          tenantId={tenant.id}
          customersGateway={customersGateway}
          busy={busy}
          error={error}
          onCancel={() => setPackageEditor(null)}
          onSave={(input) => void savePackage(input)}
        />
      ) : null}

      {deliveryEditor ? (
        <DeliveryDialog
          order={deliveryEditor}
          busy={busy}
          error={error}
          onCancel={() => setDeliveryEditor(null)}
          onSave={(order, delivery) => void saveDelivery(order, delivery)}
        />
      ) : null}

      {saleConfirm ? (
        <SaleConfirmDialog
          offer={saleConfirm.offer}
          customerAddress={saleConfirm.address}
          busy={busy}
          error={error}
          onCancel={() => setSaleConfirm(null)}
          onConfirm={(input) => void confirmSale(saleConfirm.offer, input)}
        />
      ) : null}
      {offerContextMenu}
      {offerDeleteDialog}
    </div>
  );
}
